import paypalSmartButtons from './../paypal-smart-buttons/index';
import { getSessionToken } from './../paypal-smart-buttons/paypal-utils';
const { DELIVERY_TYPE_FILTER } = require('./../paypal-smart-buttons/paypal-constants');
import CreateOrder from './../paypal-smart-buttons/create-order';
import ApproveOrder from './../paypal-smart-buttons/approve-order';
import CancelOrder from './../paypal-smart-buttons/cancel';
const UpdateOrder = require('./../google-pay-button/update');
import getShippingMethods from './../google-pay-button/shipping';
let shippingInformation = {
	countryCode: '',
	postalCode: '',
	state: '',
};
let shippingId = null;


/**
 * Check if paypal apple pay sdk is loaded
 * @public
 * @returns {boolean} Returns TRUE if paypal sdk is loaded
 */
const applePayReady = () => {
	try {
		return window.ApplePaySession && window.paypal_sdk.Applepay(); // eslint-disable-line
	} catch (error) {
		return false;
	}
};

/**
 * Initialize Paypal Apple Pay Button
 * @public
 * @param {Object | string} elemOrSelector Either dom element(s), or a css selector, targeted for smart button insertion
 * @param {Object} opts Style options used to style and format the paypal smart buttons
 * @param {function} callback Callback function to call once the button has been rendered
 * @param {number} retry The number of times to attempt to reload the buttons. Useful if we need to wait for the SDK to completly load
 * @returns {void}
 */
const initApplePayButton = (elemOrSelector, opts, callback, retry = 10) => {
	paypalSmartButtons.waitForPaypalScript(retry, () => {
		waitForApplePayScript(retry, () => {
			renderButton(elemOrSelector, opts);
		});
	});
};

const waitForApplePayScript = (attempts, callback, error) => {
	const applePayIsReady = applePayReady();

	if (!applePayIsReady && attempts > 0) {
		setTimeout(() => {
			waitForApplePayScript(attempts - 1, callback, error);
		}, 150);
		return;
	} else if (!applePayIsReady && attempts === 0) {
		return;
	}

	callback();
};

/**
 * Initialize an Apple Pay Button
 * @public
 * @param {string} selector Id selector, targeted for button insertion
 * @returns {void}
 */
const renderButton = (selector, opts) => {
	if (!window.ApplePaySession) {
		return;
	}

	if (!ApplePaySession.canMakePayments()) { // eslint-disable-line no-undef
		return;
	}

	const applePay = window.paypal_sdk.Applepay();

	applePay.config()
		.then((applePayConfig) => {
			if (applePayConfig.isEligible) {
				showButton(selector, applePayConfig, applePay, opts.loadingCallback);
			}
		});
};

/**
 * Append Apple Pay Button to DOM
 * @public
 * @param {string} selector Id selector, targeted for button insertion
 * @returns {void}
 */

const showButton = (selector, applePayConfig, applePay, loadingCallback) => {
	document.querySelector(selector).innerHTML = '<apple-pay-button id="btn-apple-pay" buttonstyle="black" type="plain" locale="en"></apple-pay-button>';
	document.getElementById('btn-apple-pay').addEventListener('click', function () {
		handleApplePayButtonClick(applePayConfig, applePay, loadingCallback);

	});
};

const getPaymentRequest = (applePayConfig) => {
	return {
		countryCode: applePayConfig.countryCode,
		merchantCapabilities: applePayConfig.merchantCapabilities,
		supportedNetworks: applePayConfig.supportedNetworks,
		currencyCode: window.rp_app.cart._currency_code,
		requiredShippingContactFields: ['name', 'phone', 'email', 'postalAddress'],
		requiredBillingContactFields: ['postalAddress'],
		total: {
			label: 'Your Order',
			type: 'final',
			amount: window.rp_app.cart.subtotal
		}
	};
};

const actions = {
	redirect: (url) => {
		window.location.href = url;
	}
};

function sortShippingMethods(methods) {
	return methods
		// Sort options by ascending amount
		.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))
		// Move pre-defined non shipping items to end of array (Pickup)
		.filter(method => !DELIVERY_TYPE_FILTER.includes(method.deliveryType))
		.concat(methods.filter(method => DELIVERY_TYPE_FILTER.includes(method.deliveryType)));
}

const handleApplePayButtonClick = (applePayConfig, applePay, loadingCallback) => {
	const paymentRequest = getPaymentRequest(applePayConfig);
	let taxAmount = 0;
	let deliveryFee = 0;

	CreateOrder({trigger_begin_checkout_event: true});

	const session = new ApplePaySession(3, paymentRequest); // eslint-disable-line no-undef

	session.onvalidatemerchant = (event) => {
		applePay.validateMerchant({
			validationUrl: event.validationURL,
			displayName: '',
		})
			.then((validResult) => {
				session.completeMerchantValidation(validResult.merchantSession);
			})
			.catch(() => {
				session.abort();
			});
	};

	session.onshippingcontactselected = (event) => {
		const countryCode = event.shippingContact.countryCode;
		const postalCode = event.shippingContact.postalCode;
		const state = event.shippingContact.administrativeArea;

		shippingInformation.countryCode = countryCode;
		shippingInformation.postalCode = postalCode;
		shippingInformation.state = state;

		getShippingMethods(countryCode, postalCode)
			.then((data) => {
				const shippingOptions = data.shippingMethods.methods.map((shippingMethod) => ({
					identifier: `${shippingMethod.id}`,
					label: `${shippingMethod.deliveryTypeLabel}`,
					detail: shippingMethod.deliveryWindowLabel ?`Est. ${shippingMethod.deliveryWindowLabel}` : '',
					amount: `${shippingMethod.amount}`
				}));

				const shippingOptionsSorted = sortShippingMethods(shippingOptions);

				const newTotal = {
					label: 'Your Order',
					amount: data.order.totalAmount,
					type: 'final',
				};

				const newLineItems = [
					{
						type: 'final',
						label: 'Your Order',
						amount: data.order.totalAmount
					},
					{
						type: 'final',
						label: 'Taxes',
						amount: data.order.taxAmount
					},
					{
						type: 'final',
						label: 'Shipping',
						amount: data.order.shippingAmount
					}
				];

				if (data.order.deliveryFeeAmount) {
					newLineItems.push({
						type: 'final',
						label: state === 'MN' ? 'Road Improvement and Food Delivery Fee' : 'Retail Delivery Fee',
						amount: data.order.deliveryFeeAmount
					});
				}

				taxAmount = data.order.taxAmount;
				session.completeShippingContactSelection(ApplePaySession.STATUS_SUCCESS, shippingOptionsSorted, newTotal, newLineItems); // eslint-disable-line no-undef
			});
	};

	session.onshippingmethodselected = (event) => {
		const shippingMethodId = event.shippingMethod.identifier;
		const shippingMethodPrice = event.shippingMethod.amount;
		const state = shippingInformation.state;

		getShippingMethods(null, null, {id: shippingMethodId, value: shippingMethodPrice})
			.then((data) => {
				const newTotal = {
					type: 'final',
					label: 'Your Order',
					amount: data.order.totalAmount
				};

				const newLineItems = [
					{
						type: 'final',
						label: 'Your Order',
						amount: data.order.subtotalAmount
					},
					{
						type: 'final',
						label: 'Taxes',
						amount: data.order.taxAmount
					},
					{
						type: 'final',
						label: 'Shipping',
						amount: data.order.shippingAmount
					}
				];

				if (data.order.deliveryFeeAmount) {
					newLineItems.push({
						type: 'final',
						label: state === 'MN' ? 'Road Improvement and Food Delivery Fee' : 'Retail Delivery Fee',
						amount: data.order.deliveryFeeAmount
					});
					deliveryFee = data.order.deliveryFeeAmount;
				}

				session.completeShippingMethodSelection(ApplePaySession.STATUS_SUCCESS, newTotal, newLineItems); // eslint-disable-line no-undef

				shippingId = shippingMethodId;
			});

	};

	session.onpaymentauthorized = (event) => {
		const cancelOrder = CancelOrder(loadingCallback);
		const approveOrder = ApproveOrder(loadingCallback);

		const shippingContact = event.payment.shippingContact;
		const billingContact = event.payment.billingContact;
		const safeAddressLine = (contact) => {
			const line = Array.isArray(contact.addressLines) && contact.addressLines.length > 0
				? contact.addressLines[0]
				: null;
			return line || [contact.locality, contact.administrativeArea, contact.postalCode].filter(Boolean).join(', ') || '';
		};

		const _shippingInformation = {
			shippingAddress: {
				name: `${shippingContact.givenName} ${shippingContact.familyName}`,
				address: safeAddressLine(shippingContact),
				city: shippingContact.locality,
				state: shippingContact.administrativeArea,
				postalCode: shippingContact.postalCode,
				country: shippingContact.countryCode,
				phone: shippingContact.phoneNumber,
			},
			billingAddress: {
				name: `${billingContact.givenName} ${billingContact.familyName}`,
				address: safeAddressLine(billingContact),
				city: billingContact.locality,
				state: billingContact.administrativeArea,
				postalCode: billingContact.postalCode,
				country: billingContact.countryCode,
				phone: '',
			}
		};

		const customer = {
			firstName: event.payment.billingContact.givenName,
			lastName: event.payment.billingContact.familyName,
			email: event.payment.shippingContact.emailAddress,
			phone: event.payment.shippingContact.phoneNumber,
		};

		cancelOrder();

		CreateOrder({
			taxAmount: taxAmount,
			deliveryFee: deliveryFee,
			trigger_begin_checkout_event: false,
		})
			.then((orderId) => {
				applePay.confirmOrder({
					orderId: orderId,
					token: event.payment.token,
					billingContact: event.payment.billingContact
				})
					.then(() => {
						const sessionToken = getSessionToken();

						UpdateOrder({
							shippingAddress: _shippingInformation.shippingAddress,
							billingAddress: _shippingInformation.billingAddress,
							sessionToken: sessionToken,
							shippingId: shippingId,
							customer: customer,
						})
							.then((data) => {
								if (data.redirect) {
									cancelOrder();
									session.completePayment(ApplePaySession.STATUS_FAILURE); // eslint-disable-line no-undef
									actions.redirect(data.redirect);
								}

								approveOrder({
									orderID: orderId,
									gateway: 'PAYPALCP_AP'
								}, actions);
								session.completePayment(ApplePaySession.STATUS_SUCCESS); // eslint-disable-line no-undef
							})
							.catch(() => {
								cancelOrder();
								session.completePayment(ApplePaySession.STATUS_FAILURE); // eslint-disable-line no-undef
							});
					})
					.catch(() => {
						session.completePayment(ApplePaySession.STATUS_FAILURE); // eslint-disable-line no-undef
						cancelOrder();
					});
			})
			.catch(() => {
				session.completePayment(ApplePaySession.STATUS_FAILURE); // eslint-disable-line no-undef

				session.abort();
			});
	};

	session.begin();
};


export default {
	initApplePayButton,
};
